import { supabase } from '../lib/supabase'
import type { 
  Task, 
  CreateTaskData, 
  UpdateTaskData, 
  TaskStats, 
  TaskFilters, 
  WeeklyTaskLimits,
  AITaskSuggestion,
  TaskTemplate,
  WeekInfo
} from '../types/task'
import { getWeekInfo } from '../types/task'

export const taskService = {
  // Get tasks for user with optional filters
  async getTasks(userId: string, filters?: TaskFilters): Promise<{ success: boolean; tasks?: Task[]; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      let query = supabase
        .from('tasks')
        .select(`
          *,
          client:clients(id, name, company),
          shoot:shoots(id, title, date)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      // Apply filters
      if (filters?.status?.length) {
        query = query.in('status', filters.status)
      }
      if (filters?.priority?.length) {
        query = query.in('priority', filters.priority)
      }
      if (filters?.category?.length) {
        query = query.in('category', filters.category)
      }
      if (filters?.clientId) {
        query = query.eq('client_id', filters.clientId)
      }
      if (filters?.shootId) {
        query = query.eq('shoot_id', filters.shootId)
      }
      if (filters?.weekStartDate) {
        query = query.eq('week_start_date', filters.weekStartDate)
      }
      if (filters?.dueDate?.from) {
        query = query.gte('due_date', filters.dueDate.from)
      }
      if (filters?.dueDate?.to) {
        query = query.lte('due_date', filters.dueDate.to)
      }

      const { data, error } = await query

      if (error) throw error

      // Apply search filter client-side for better performance
      let tasks = data || []
      if (filters?.search) {
        const searchLower = filters.search.toLowerCase()
        tasks = tasks.filter(task => 
          task.title.toLowerCase().includes(searchLower) ||
          task.description?.toLowerCase().includes(searchLower) ||
          task.tags?.some((tag: string) => tag.toLowerCase().includes(searchLower))
        )
      }

      return { success: true, tasks }
    } catch (error) {
      console.error('Error fetching tasks:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  },

  // Get tasks for current week
  async getCurrentWeekTasks(userId: string): Promise<{ success: boolean; tasks?: Task[]; weekInfo?: WeekInfo; error?: string }> {
    try {
      const weekInfo = getWeekInfo()
      const result = await this.getTasks(userId, { weekStartDate: weekInfo.weekStartDate })
      
      if (result.success) {
        return { success: true, tasks: result.tasks, weekInfo }
      }
      return result
    } catch (error) {
      console.error('Error fetching current week tasks:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  },

  // Get weekly task limits for user
  async getWeeklyLimits(userId: string, subscriptionStatus: string = 'freemium'): Promise<WeeklyTaskLimits> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const weekInfo = getWeekInfo()
      const isPremium = subscriptionStatus === 'professional' || subscriptionStatus === 'studio'
      
      // Count tasks for current week
      const { data, error } = await supabase
        .from('tasks')
        .select('id')
        .eq('user_id', userId)
        .eq('week_start_date', weekInfo.weekStartDate)

      if (error) throw error

      const currentWeekUsage = data?.length || 0
      
      return {
        weekStartDate: weekInfo.weekStartDate,
        weekNumber: weekInfo.weekNumber,
        year: weekInfo.year,
        currentWeekUsage,
        maxTasksPerWeek: isPremium ? 999999 : 30, // Use large number for display
        canCreateTask: isPremium || currentWeekUsage < 30,
        isUnlimited: isPremium
      }
    } catch (error) {
      console.error('Error fetching weekly limits:', error)
      // Return safe defaults
      const weekInfo = getWeekInfo()
      return {
        weekStartDate: weekInfo.weekStartDate,
        weekNumber: weekInfo.weekNumber,
        year: weekInfo.year,
        currentWeekUsage: 0,
        maxTasksPerWeek: 30,
        canCreateTask: true,
        isUnlimited: false
      }
    }
  },

  // Create new task
  async createTask(userId: string, taskData: CreateTaskData): Promise<{ success: boolean; task?: Task; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      // Check weekly limits first
      const limits = await this.getWeeklyLimits(userId)
      if (!limits.canCreateTask) {
        return { success: false, error: 'Weekly task limit reached. Upgrade to Pro for unlimited tasks.' }
      }

      const weekInfo = getWeekInfo()
      
      const newTask = {
        user_id: userId,
        title: taskData.title,
        description: taskData.description,
        priority: taskData.priority,
        status: 'pending' as const,
        category: taskData.category,
        due_date: taskData.dueDate,
        estimated_minutes: taskData.estimatedMinutes,
        tags: taskData.tags || [],
        client_id: taskData.clientId,
        shoot_id: taskData.shootId,
        invoice_id: taskData.invoiceId,
        week_start_date: weekInfo.weekStartDate,
        week_number: weekInfo.weekNumber,
        year: weekInfo.year,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }

      const { data, error } = await supabase
        .from('tasks')
        .insert(newTask)
        .select(`
          *,
          client:clients(id, name, company),
          shoot:shoots(id, title, date)
        `)
        .single()

      if (error) throw error

      return { success: true, task: data }
    } catch (error) {
      console.error('Error creating task:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  },

  // Update task
  async updateTask(taskId: string, updates: UpdateTaskData): Promise<{ success: boolean; task?: Task; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const updateData: any = {
        ...updates,
        updated_at: new Date().toISOString()
      }

      // If marking as completed, set completion timestamp
      if (updates.status === 'completed') {
        updateData.completed_at = new Date().toISOString()
      }

      // Convert camelCase to snake_case for database
      const dbUpdateData = {
        title: updateData.title,
        description: updateData.description,
        priority: updateData.priority,
        status: updateData.status,
        category: updateData.category,
        due_date: updateData.dueDate,
        estimated_minutes: updateData.estimatedMinutes,
        actual_minutes: updateData.actualMinutes,
        tags: updateData.tags,
        client_id: updateData.clientId,
        shoot_id: updateData.shootId,
        invoice_id: updateData.invoiceId,
        updated_at: updateData.updated_at,
        completed_at: updateData.completed_at
      }

      // Remove undefined values
      Object.keys(dbUpdateData).forEach(key => {
        if ((dbUpdateData as any)[key] === undefined) {
          delete (dbUpdateData as any)[key]
        }
      })

      const { data, error } = await supabase
        .from('tasks')
        .update(dbUpdateData)
        .eq('id', taskId)
        .select(`
          *,
          client:clients(id, name, company),
          shoot:shoots(id, title, date)
        `)
        .single()

      if (error) throw error

      return { success: true, task: data }
    } catch (error) {
      console.error('Error updating task:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  },

  // Delete task
  async deleteTask(taskId: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId)

      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error deleting task:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  },

  // Get task statistics
  async getTaskStats(userId: string): Promise<{ success: boolean; stats?: TaskStats; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('user_id', userId)

      if (error) throw error

      const tasks = data || []
      const weekInfo = getWeekInfo()
      const thisWeekTasks = tasks.filter(task => task.week_start_date === weekInfo.weekStartDate)
      
      // Calculate completion times for completed tasks
      const completedTasks = tasks.filter(task => task.status === 'completed' && task.actual_minutes)
      const averageCompletionTime = completedTasks.length > 0 
        ? completedTasks.reduce((sum, task) => sum + (task.actual_minutes || 0), 0) / completedTasks.length
        : 0

      // Calculate productivity score (completion rate + timeliness)
      const completionRate = tasks.length > 0 ? (tasks.filter(task => task.status === 'completed').length / tasks.length) * 100 : 0
      const overdueCount = tasks.filter(task => 
        task.due_date && 
        new Date(task.due_date) < new Date() && 
        task.status !== 'completed'
      ).length
      const timelinessScore = tasks.length > 0 ? Math.max(0, 100 - (overdueCount / tasks.length) * 100) : 0
      const productivityScore = tasks.length > 0 ? Math.round((completionRate + timelinessScore) / 2) : 0

      const stats: TaskStats = {
        totalTasks: tasks.length,
        completedTasks: tasks.filter(task => task.status === 'completed').length,
        pendingTasks: tasks.filter(task => task.status === 'pending').length,
        inProgressTasks: tasks.filter(task => task.status === 'in_progress').length,
        overdueTasks: overdueCount,
        thisWeekTasks: thisWeekTasks.length,
        thisWeekCompleted: thisWeekTasks.filter(task => task.status === 'completed').length,
        averageCompletionTime,
        productivityScore
      }

      return { success: true, stats }
    } catch (error) {
      console.error('Error fetching task stats:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  },

  // AI-powered task suggestions (Premium feature)
  async getAITaskSuggestions(_userId: string, subscriptionStatus: string): Promise<{ success: boolean; suggestions?: AITaskSuggestion[]; error?: string }> {
    try {
      const isPremium = subscriptionStatus === 'professional' || subscriptionStatus === 'studio'
      
      if (!isPremium) {
        return { success: false, error: 'AI task suggestions are available for Premium users only' }
      }

      // For now, return mock suggestions. In a real implementation, this would call an AI service
      const mockSuggestions: AITaskSuggestion[] = [
        {
          id: 'ai-1',
          title: 'Prepare equipment for upcoming wedding shoot',
          description: 'Check camera batteries, clean lenses, and pack backup equipment for the Johnson wedding this weekend.',
          priority: 'high',
          category: 'shoot_prep',
          estimatedMinutes: 45,
          reasoning: 'You have a wedding shoot scheduled in 3 days. Based on your past patterns, equipment prep is usually done 1-2 days before.',
          confidence: 95,
          basedOn: 'shoot',
          relatedEntityId: 'shoot-123'
        },
        {
          id: 'ai-2',
          title: 'Follow up with Sarah about portrait session payment',
          description: 'Invoice was sent 5 days ago and payment is due tomorrow. Send a friendly reminder.',
          priority: 'medium',
          category: 'client_communication',
          estimatedMinutes: 15,
          reasoning: 'Invoice payment is due soon and no payment has been received. Following up now prevents overdue situations.',
          confidence: 88,
          basedOn: 'deadline',
          relatedEntityId: 'invoice-456'
        },
        {
          id: 'ai-3',
          title: 'Edit and deliver photos from last week\'s corporate event',
          description: 'Process and edit the 200+ photos from the TechCorp annual meeting. Deliver via client portal.',
          priority: 'urgent',
          category: 'editing',
          estimatedMinutes: 240,
          reasoning: 'Photos were taken 5 days ago. Based on your typical delivery timeline, these should be completed soon.',
          confidence: 92,
          basedOn: 'pattern',
          relatedEntityId: 'shoot-789'
        }
      ]

      return { success: true, suggestions: mockSuggestions }
    } catch (error) {
      console.error('Error fetching AI suggestions:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  },

  // Create task from AI suggestion
  async createTaskFromAISuggestion(userId: string, suggestion: AITaskSuggestion): Promise<{ success: boolean; task?: Task; error?: string }> {
    try {
      const taskData: CreateTaskData = {
        title: suggestion.title,
        description: suggestion.description,
        priority: suggestion.priority,
        category: suggestion.category,
        estimatedMinutes: suggestion.estimatedMinutes
      }

      // Add related entity if available
      if (suggestion.basedOn === 'shoot' && suggestion.relatedEntityId) {
        taskData.shootId = suggestion.relatedEntityId
      } else if (suggestion.basedOn === 'deadline' && suggestion.relatedEntityId) {
        taskData.invoiceId = suggestion.relatedEntityId
      }

      const result = await this.createTask(userId, taskData)
      
      if (result.success && result.task) {
        // Mark as AI-generated
        await this.updateTask(result.task.id, { 
          // Note: We'd need to add isAiGenerated to UpdateTaskData interface
          // For now, we'll track this in tags
          tags: [...(result.task.tags || []), 'ai-generated']
        })
      }

      return result
    } catch (error) {
      console.error('Error creating task from AI suggestion:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  },

  // Get task templates (Premium feature)
  async getTaskTemplates(userId: string): Promise<{ success: boolean; templates?: TaskTemplate[]; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { data, error } = await supabase
        .from('task_templates')
        .select('*')
        .or(`user_id.eq.${userId},user_id.is.null`) // User's templates + global templates
        .order('created_at', { ascending: false })

      if (error) throw error

      return { success: true, templates: data || [] }
    } catch (error) {
      console.error('Error fetching task templates:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  },

  // Bulk operations
  async bulkUpdateTasks(taskIds: string[], updates: Partial<UpdateTaskData>): Promise<{ success: boolean; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const updateData = {
        ...updates,
        updated_at: new Date().toISOString()
      }

      const { error } = await supabase
        .from('tasks')
        .update(updateData)
        .in('id', taskIds)

      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error bulk updating tasks:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  },

  // Get overdue tasks
  async getOverdueTasks(userId: string): Promise<{ success: boolean; tasks?: Task[]; error?: string }> {
    try {
      const today = new Date().toISOString().split('T')[0]
      return await this.getTasks(userId, {
        status: ['pending', 'in_progress'],
        dueDate: { to: today }
      })
    } catch (error) {
      console.error('Error fetching overdue tasks:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  }
} 